import type {
  SupplyCorrection,
  SupplyCorrectionParams,
  SupplyTimelineParams,
  SupplyTimelineResponse,
} from '@gozoki/api-types'
import {
  keepPreviousData,
  type UseMutationResult,
  type UseQueryResult,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import {
  fetchStoreSupplyTimeline,
  fetchSupplyTimeline,
  putSupplyCorrection,
} from '../queries/supply.queries'

export const useSupplyTimeline = (
  selection: SupplyTimelineParams,
  enabled = true,
): UseQueryResult<SupplyTimelineResponse, unknown> => {
  const normalizedScope =
    selection.targetId !== undefined ? (selection.scope ?? 'category') : selection.scope

  return useQuery({
    enabled,
    placeholderData: keepPreviousData,
    queryFn: () => fetchSupplyTimeline(selection),
    queryKey: [
      'supply-timeline',
      selection.storeId,
      selection.days,
      normalizedScope,
      selection.targetId,
      selection.corrections !== false,
    ],
    refetchInterval: 5 * 60_000,
  })
}

// store-app : timeline de la boutique connectée (guard `store`). Pas de storeId (dérivé du token).
export const useStoreSupplyTimeline = (
  selection: Omit<SupplyTimelineParams, 'storeId'>,
  enabled = true,
): UseQueryResult<SupplyTimelineResponse, unknown> => {
  const normalizedScope =
    selection.targetId !== undefined ? (selection.scope ?? 'category') : selection.scope

  return useQuery({
    enabled,
    placeholderData: keepPreviousData,
    queryFn: () => fetchStoreSupplyTimeline(selection),
    queryKey: [
      'store-supply-timeline',
      selection.days,
      normalizedScope,
      selection.targetId,
      selection.corrections !== false,
    ],
    refetchInterval: 5 * 60_000,
  })
}

export const useSetSupplyCorrection = (): UseMutationResult<
  SupplyCorrection,
  unknown,
  SupplyCorrectionParams
> => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: putSupplyCorrection,
    onSuccess: async (_data, variables) => {
      await queryClient.invalidateQueries({
        queryKey: ['supply-timeline', variables.storeId],
      })
    },
  })
}
